<template>
  <DetailViewShell :dv="dv">
    <template #default>
      <div class="tabs is-boxed mb-0">
        <ul>
          <li
            v-for="tab in sortedTabs"
            :key="`tabs-tab-${tab.id}`"
            :class="{ 'is-active': activeTabId === tab.id }"
            @click="handleChangeTab(tab.id)"
          >
            <a>
              <span class="icon is-small">
                <i :class="tab.icon" aria-hidden="true"></i>
              </span>
              <span>{{ TABS[tab.id].name }}</span>
            </a>
          </li>
        </ul>
      </div>

      <div class="tabs-content box">
        <!-- Details -->
        <div v-if="activeTabId === 'details'" class="tab-body">
          <div class="record-panels">
            <DefaultPanel :is-collapsible="false">
              <template #header>
                <span class="icon">
                  <i class="fa-solid fa-barcode" />
                </span>
              </template>
              <template
                v-if="!dv.isEditing && dv.recordStatus"
                #header-controls
              >
                <StatusWidget
                  :status="dv.recordStatus"
                  :field-meta="dv.modelAttributes[dv.recordStatusKey]"
                />
              </template>
              <RecordFieldSections :dv="dv">
                <template v-if="!dv.isEditing" #overview-right></template>
              </RecordFieldSections>
            </DefaultPanel>
            <div class="sidebar-panels">
              <RocrateSection
                v-if="!dv.isDeleted"
                app-name="workflows"
                model-name="workflowruns"
                :record-id="id"
                :is-imported="Boolean(record.is_imported_rocrate?.value)"
              />
              <AnnotationList
                v-if="dv.isAnnotatable"
                :id="id"
                :app-name="appName"
                :model-name="modelName"
                :can-edit="dv.canEdit"
                :is-editing="dv.isEditing"
                @delete="(id) => dv.removeField(id)"
              />
              <NoteList
                v-if="dv.isNoteable"
                :can-add-note="true"
                :editable="true"
                :app-name="appName"
                :model-name="modelName"
                :model-type="modelType"
              />
            </div>
          </div>
        </div>

        <!-- Command Line -->
        <div v-else-if="activeTabId === 'command_line'" class="tab-body">
          <FieldGrid class="cmdline" :fields="cmdlineGrid" :dv="dv" />
        </div>

        <!-- Config -->
        <fieldset
          v-else-if="activeTabId === 'config'"
          class="tab-body"
          :disabled="isLinkingData || isUnlinkingData"
        >
          <WorkflowRunDatasetSection
            :datasets="configFiles"
            :can-edit="dv.canEdit"
            @unlink-dataset="(d) => unlinkData([d])"
            @selected="(d) => (selectedRecords = d)"
          >
            <template #title>
              <h2 class="title is-size-6 m-0">
                The following have been imported as part of the Workflow Run ({{
                  configFiles.length
                }})
              </h2>
            </template>
            <template #controls>
              <button
                class="button is-small is-primary is-outlined mr-2"
                @click="isOpenRecordSelectionModal = true"
              >
                <span class="icon"><i class="fas fa-link"></i></span>
                <span>Link Config Dataset</span>
              </button>
              <Popover
                v-if="configFiles.length"
                ref="unlinkConfigPopoverRef"
                class="ml-auto"
              >
                <button
                  v-tooltip="unlinkTooltip"
                  class="button is-small is-danger"
                  :class="{ 'is-outlined': selectedRecordsCount === 0 }"
                  :disabled="!canUnlinkData"
                >
                  <span class="icon"><i class="fas fa-unlink"></i></span>
                  <span>Unlink</span>
                  <span v-if="selectedRecordsCount > 0">
                    {{ selectedRecordsCount }}
                  </span>
                  {{ pluralize('Dataset', selectedRecordsCount) }}
                </button>
                <template #popper>
                  <div>
                    <p class="mb-3">
                      <span class="icon has-text-info">
                        <i class="fas fa-info-circle"></i>
                      </span>
                      This will only remove the association between this run and
                      the datasets. The datasets will not be deleted
                    </p>
                    <button
                      class="button is-danger is-outlined is-small is-fullwidth"
                      :class="{ 'is-loading': isUnlinkingData }"
                      :disabled="isUnlinkingData"
                      @click="unlinkData(selectedRecords)"
                    >
                      <span class="icon">
                        <i class="fa-solid fa-unlink"></i>
                      </span>
                      <span>Unlink</span>
                    </button>
                  </div>
                </template>
              </Popover>
            </template>
            <template #unlink-control>
              <button
                v-tooltip="unlinkTooltip"
                class="button is-small is-danger is-outlined is-rounded ml-2"
                popper-class="is-danger"
                :disabled="isImportedROCrate"
              >
                <span class="icon"><i class="fa-solid fa-unlink"></i></span>
              </button>
            </template>
          </WorkflowRunDatasetSection>
          <template v-if="workflowVersionFiles.length > 0">
            <h2 class="title is-size-6">
              Default config files that may have been applied ({{
                workflowVersionFiles.length
              }})
            </h2>
            <p class="help">
              These are configs part of the particular Workflow Version.
            </p>
            <ol>
              <li v-for="f of workflowVersionFiles" :key="`config-${f.id}`">
                <WorkflowRunDatasetPeek v-bind="f" />
              </li>
            </ol>
          </template>
        </fieldset>

        <!-- Reports / Logs / Inputs / Outputs: unified header + toolbar -->
        <template
          v-else-if="
            ['reports', 'logs', 'inputs', 'outputs'].includes(activeTabId)
          "
        >
          <div :class="{ 'is-busy': isLinkingData || isUnlinkingData }">
            <div class="tab-controls">
              <h2 class="title is-size-6 m-0">{{ TABS[activeTabId].name }}</h2>
              <div class="is-flex">
                <button
                  v-tooltip="
                    isImportedROCrate &&
                    `New ${TABS[activeTabId].name.toLowerCase()} cannot be added to an imported RO-Crate`
                  "
                  class="button is-primary is-small is-outlined mr-2"
                  :disabled="isImportedROCrate"
                  @click="isOpenRecordSelectionModal = true"
                >
                  <span class="icon"><i class="fas fa-link"></i></span>
                  <span>Link {{ TABS[activeTabId].name }} Dataset</span>
                </button>
                <Popover ref="unlinkDatasetPopoverRef" class="ml-auto">
                  <button
                    v-tooltip="unlinkTooltip"
                    class="button is-small is-danger"
                    :class="{ 'is-outlined': !canUnlinkData }"
                    :disabled="!canUnlinkData"
                  >
                    <span class="icon"><i class="fas fa-unlink"></i></span>
                    <span>Unlink</span>
                    <span v-if="selectedRecordsCount > 0">
                      {{ selectedRecordsCount }}
                    </span>
                    {{ pluralize('Dataset', selectedRecordsCount) }}
                  </button>
                  <template #popper>
                    <p class="mb-3">
                      <span class="icon has-text-info">
                        <i class="fas fa-info-circle"></i>
                      </span>
                      This will only remove the association between this run and
                      its
                      {{ TABS[activeTabId].name.toLowerCase() }}. The datasets
                      will not be deleted.
                    </p>
                    <button
                      class="button is-danger is-outlined is-small is-fullwidth"
                      :class="{ 'is-loading': isUnlinkingData }"
                      :disabled="isUnlinkingData"
                      @click="unlinkData(selectedRecords)"
                    >
                      <span class="icon">
                        <i class="fa-solid fa-unlink"></i>
                      </span>
                      <span>Unlink</span>
                    </button>
                  </template>
                </Popover>
              </div>
            </div>
            <!-- Dataset peek list (reports / logs) -->
            <WorkflowRunDatasetSection
              v-if="activeTabId === 'reports' || activeTabId === 'logs'"
              :datasets="activeDatasetFiles"
              :can-edit="dv.canEdit"
              @unlink-dataset="(d) => unlinkData([d])"
              @selected="(d) => (selectedRecords = d)"
            >
              <template #unlink-control>
                <button
                  v-tooltip="unlinkTooltip"
                  class="button is-small is-danger is-outlined is-rounded ml-2"
                  popper-class="is-danger"
                  :disabled="isImportedROCrate"
                >
                  <span class="icon"><i class="fa-solid fa-unlink"></i></span>
                </button>
              </template>
            </WorkflowRunDatasetSection>
            <!-- Injected list (inputs / outputs) -->
            <ListComponent
              v-else
              ref="inputListRef"
              :key="`wfrundata-${activeTabId}-${refreshKey}`"
              class="injected-list edge-to-edge no-padding"
              app-name="workflows"
              model-name="workflowrundatasets"
              model-type="default"
              vuex-prefix="wfrundetail-input-"
              :is-compact="true"
              :show-add-controls="false"
              :show-global-search="false"
              :show-owner-controls="false"
              :show-quick-actions="false"
              :can-save-settings="true"
              :is-batch-unlinkable="false"
              :init-context="injectedListInitContext"
              init-ownership="all"
              :column-names="['dataset', 'data_type', 'owner', 'workflowrun']"
              :show-deleted="dv.isDeleted"
              @selected="(d) => (selectedRecords = d)"
            />
          </div>
        </template>
      </div>

      <RecordPickerModal
        v-model="isOpenRecordSelectionModal"
        :title="`Link ${dataType?.toLowerCase() || ''} datasets`"
        app-name="data_management"
        model-name="datasets"
        mode="search"
        @select="handleBulkDataLink"
      >
        <template #search-header>
          <span class="title is-size-5 mb-0">
            Select datasets to link as {{ dataType?.toLowerCase() }}
          </span>
        </template>
      </RecordPickerModal>
    </template>
  </DetailViewShell>
</template>

<script>
import pluralize from 'pluralize'
import { computed, ref, watch } from 'vue'

import { del, get, put } from '@/api'
import { useDetailView } from '@/composables/useDetailView'
import store from '@/store'
import { momentDate } from '@/utils'

import AnnotationList from '@/components/AnnotationList.vue'
import DefaultPanel from '@/components/DefaultPanel.vue'
import RecordFieldSections from '@/components/detail/shared/RecordFieldSections.vue'
import DetailViewShell from '@/components/DetailViewShell.vue'
import FieldGrid from '@/components/fields/FieldGrid.vue'
import NoteList from '@/components/NoteList.vue'
import RecordPickerModal from '@/components/RecordPickerModal.vue'
import RocrateSection from '@/components/RocrateSection.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import WorkflowRunDatasetPeek from '@/components/WorkflowRunDatasetPeek.vue'
import WorkflowRunDatasetSection from '@/components/WorkflowRunDatasetSection.vue'

const TABS = {
  details: {
    name: 'Details',
    icon: 'fa-solid fa-barcode',
    order: 1,
  },
  command_line: {
    name: 'Command Line',
    icon: 'fa-solid fa-terminal',
    order: 2,
  },
  config: {
    name: 'Config',
    icon: 'fa-solid fa-cog',
    order: 3,
    reloadRecord: true,
  },
  inputs: {
    name: 'Inputs',
    icon: 'fa-solid fa-file-import',
    order: 4,
  },
  outputs: {
    name: 'Outputs',
    icon: 'fa-solid fa-file-export',
    order: 5,
  },
  reports: {
    name: 'Reports',
    icon: 'fa-solid fa-file-invoice',
    order: 6,
    reloadRecord: true,
  },
  logs: {
    name: 'Logs',
    icon: 'fa-solid fa-receipt',
    order: 7,
    reloadRecord: true,
  },
}

const DATATYPE_ID_TO_DATATYPE = {
  config: 'CONFIG',
  inputs: 'INPUT',
  outputs: 'OUTPUT',
  reports: 'REPORT',
  logs: 'LOG',
}

export default {
  name: 'WorkflowRunDetailView',
  components: {
    AnnotationList,
    DefaultPanel,
    DetailViewShell,
    RecordFieldSections,
    FieldGrid,
    RecordPickerModal,
    RocrateSection,
    NoteList,
    StatusWidget,
    WorkflowRunDatasetPeek,
    WorkflowRunDatasetSection,
  },
  props: {
    appName: { type: String, required: true },
    modelName: { type: String, required: true },
    modelType: { type: String, required: true },
    id: { type: String, required: true },
  },
  setup(props) {
    const dv = useDetailView({
      appName: props.appName,
      modelName: props.modelName,
      modelType: props.modelType,
      id: props.id,
    })

    const record = computed(() => dv.record || {})
    const recordId = computed(() => dv.recordId || '')
    const loadRecord = () => dv.loadRecord()

    // --- Tabs ---
    const activeTabId = ref('details')
    const cmdlineFieldNames = ['run_command', 'run_environment']
    const refreshKey = ref(0)
    const isLinkingData = ref(false)
    const isUnlinkingData = ref(false)

    const sortedTabs = computed(() =>
      Object.entries(TABS)
        .map(([id, v]) => ({ id, ...v }))
        .sort((a, b) => a.order - b.order),
    )

    const cmdlineGrid = computed(() =>
      cmdlineFieldNames
        .filter((name) => dv.modelAttributes[name])
        .map((name) => [name]),
    )

    const workflowVersionId = computed(() => {
      return record.value?.workflowversion?.value?.id || null
    })

    const workflowRunDatasets = computed(() => {
      const ds = record.value?.workflowrundatasets?.value || []
      return ds.map((d) => ({
        id: d.id,
        name: d.name,
        dataset: d.dataset,
        created: d.created ? momentDate(d.created) : null,
        modified: d.modified ? momentDate(d.modified) : null,
        createdBy: d.created_by || { username: 'Unknown' },
        modifiedBy: d.modified_by || { username: 'Unknown' },
        dataType: d.data_type?.value,
        peekUrl: `data_management/datasets/${d.dataset.id}/peek`,
      }))
    })

    const filterDatasets = (neededTypes) => (dataset) => {
      const dataType = dataset.dataType?.toLowerCase()
      return neededTypes.some((_type) => dataType === _type.toLowerCase())
    }

    const configFiles = computed(() =>
      workflowRunDatasets.value.filter(filterDatasets(['CONFIG', 'PARAMETER'])),
    )
    const logFiles = computed(() =>
      workflowRunDatasets.value.filter(filterDatasets(['LOG'])),
    )
    const reportFiles = computed(() =>
      workflowRunDatasets.value.filter(filterDatasets(['REPORT'])),
    )

    const activeDatasetFiles = computed(() => {
      if (activeTabId.value === 'reports') return reportFiles.value
      if (activeTabId.value === 'logs') return logFiles.value
      return []
    })

    function getWorkflowFiles(workflowVersionId) {
      let wfFiles = []
      try {
        const response = get('workflows/workflowfiles', {
          params: {
            workflowversion_id: workflowVersionId,
            data_type: ['CONFIG', 'PARAMETER'],
          },
          paramsSerializer: { indexes: null },
        })
        wfFiles = response.data?.results || []
      } catch (error) {
        console.error('Failed to fetch workflow files:', error)
      }
      return wfFiles
    }

    const workflowVersionFiles = ref([])
    watch(
      () => workflowVersionId.value,
      async (_workflowVersionId) => {
        if (!_workflowVersionId) return
        const rawWorkflowFiles = await getWorkflowFiles(_workflowVersionId)
        workflowVersionFiles.value = rawWorkflowFiles.map((f) => {
          const id = f.id?.value || f.id
          const peekUrl = `workflows/workflowfiles/${id}/peek`
          const dataType =
            f.data_type?.value?.value || f.data_type?.value || f.data_type
          const userUnknown = { username: 'Unknown' }
          return {
            id,
            name: f.name?.value?.name,
            created: f.created?.value ? momentDate(f.created.value) : null,
            modified: f.modified?.value ? momentDate(f.modified.value) : null,
            createdBy: f.created_by?.value || userUnknown,
            modifiedBy: f.modified_by?.value || userUnknown,
            dataType,
            peekUrl,
          }
        })
      },
      { immediate: true },
    )

    const handleChangeTab = (tabId) => {
      selectedRecords.value = []
      activeTabId.value = tabId
    }

    const dataType = computed(() => DATATYPE_ID_TO_DATATYPE[activeTabId.value])
    const injectedListInitContext = computed(() => {
      const dataTypeIdFilterKey =
        activeTabId.value === 'inputs'
          ? 'input_of_workflowrun_id'
          : 'output_of_workflowrun_id'
      const params = [
        { key: dataTypeIdFilterKey, id: recordId.value },
        { key: 'data_type', value: dataType.value },
      ]
      return { params }
    })

    const selectedRecords = ref([])
    const selectedRecordsCount = computed(() => selectedRecords.value.length)
    const isOpenRecordSelectionModal = ref(false)

    const unlinkConfigPopoverRef = ref(null)
    const unlinkDatasetPopoverRef = ref(null)

    const refresh = () => {
      if (TABS[activeTabId.value]?.reloadRecord) loadRecord()
      else refreshKey.value += 1
      unlinkConfigPopoverRef.value?.close?.()
      unlinkDatasetPopoverRef.value?.close?.()
    }

    const handleBulkDataLink = async (records) => {
      if (!records?.length) return
      try {
        isLinkingData.value = true
        await put(`workflows/workflowruns/${recordId.value}/manage_datasets/`, {
          data: {
            datasets: records.map((d) => ({
              id: d.id.value,
              name: d.name.value.name,
            })),
            workflowrun: recordId.value,
            data_type: dataType.value,
          },
        })
        store.commit('notify', {
          type: 'success',
          message: `Added ${records.length} dataset(s) successfully`,
          isDismissable: true,
        })
        refresh()
      } catch (error) {
        store.commit('notify', {
          type: 'error',
          message: error?.response?.data?.message || 'Failed to add datasets',
          isDismissable: true,
        })
      } finally {
        isLinkingData.value = false
      }
    }

    const unlinkData = async (datasets) => {
      const ids = datasets.map((d) => d.id?.value || d.id)
      try {
        isUnlinkingData.value = true
        const unlinkPromises = ids.map((id) =>
          del(`workflows/workflowrundatasets/${id}`)
            .then(() => ({ success: true, id }))
            .catch((error) => ({ success: false, id, error })),
        )
        const results = await Promise.all(unlinkPromises)
        const succeeded = results.filter((r) => r.success).map((r) => r.id)
        const failed = results.filter((r) => !r.success).map((r) => r.id)
        if (failed.length === 0) {
          store.commit('notify', {
            type: 'success',
            message: `Successfully unlinked ${succeeded.length} dataset(s)`,
            isDismissable: true,
          })
        } else {
          const successHeader =
            `Successfully unlinked ${succeeded.length} dataset(s). ` +
            'The following errors occurred while unlinking datasets:'
          const errorHeader =
            'The following errors occurred while unlinking datasets:'
          const header = succeeded.length > 0 ? successHeader : errorHeader
          const lines = failed.map((id) => {
            const error = results.find((r) => r.id === id)?.error
            const dataset = datasets.find(
              (d) => (d.id?.value || d.id) === id,
            )?.name
            const datasetName = dataset?.value?.name
              ? `Dataset "${dataset?.value?.name}"`
              : `ID ${id}`
            const errorDetail =
              error?.response?.data?.message ||
              error?.message ||
              'Unknown error'
            return `- Failed to unlink ${datasetName}: ${errorDetail}`
          })
          const errormsg = [header, ...lines].join('\n')
          store.commit('notify', {
            type: 'error',
            message: errormsg,
            isDismissable: true,
            duration: 10000,
          })
          console.error('Failed to unlink some datasets:', failed)
        }
        refresh()
      } catch (error) {
        console.error('Failed to unlink dataset:', error)
      } finally {
        isUnlinkingData.value = false
      }
    }

    const isImportedROCrate = computed(
      () => record.value?.is_imported_rocrate?.value === true,
    )

    const canUnlinkData = computed(
      () => !isImportedROCrate.value && selectedRecordsCount.value > 0,
    )
    const unlinkTooltip = computed(() => {
      const dataTypeName = TABS[activeTabId.value].name
      const roCrateMsg = `Cannot unlink ${dataTypeName.toLowerCase()} from an imported RO-Crate`
      const noSelectMsg = `No ${activeTabId.value.toLowerCase()} selected`
      if (selectedRecordsCount.value === 0) return noSelectMsg
      if (isImportedROCrate.value) return roCrateMsg
      return undefined
    })

    return {
      dv,
      record,
      recordId,
      loadRecord,
      TABS,
      DATATYPE_ID_TO_DATATYPE,
      pluralize,
      sortedTabs,
      activeTabId,
      handleChangeTab,
      cmdlineGrid,
      workflowVersionFiles,
      configFiles,
      activeDatasetFiles,
      dataType,
      injectedListInitContext,
      canUnlinkData,
      selectedRecords,
      selectedRecordsCount,
      isOpenRecordSelectionModal,
      handleBulkDataLink,
      isLinkingData,
      refreshKey,
      isUnlinkingData,
      unlinkData,
      unlinkTooltip,
      isImportedROCrate,
      unlinkConfigPopoverRef,
      unlinkDatasetPopoverRef,
    }
  },
}
</script>

<style lang="scss" scoped>
.tabs.is-boxed {
  border-top-left-radius: 6px;
  border-top-right-radius: 6px;
  background-color: var(--color-background-gray);
  ul {
    align-items: flex-end;

    & li,
    & li a {
      transition: all 0.3s;
    }

    & li a {
      padding: 0.3rem 0.8rem;
    }

    & li:first-of-type {
      margin-left: 10px;
    }
    & li {
      height: calc(100% - 3px);
    }
    & li:not(.is-active) a {
      height: 100%;
    }
    & li.is-active a {
      border-color: var(--color-border-default);
    }
    & li:not(.is-active) a:hover {
      background-color: var(--white);
    }
  }
}

div.tabs-content {
  padding: 0;

  &.box {
    border: 1px solid var(--color-border-default);
    margin-top: -1px;
    border-top-left-radius: 0 !important;
    border-top-right-radius: 0 !important;
  }

  ol {
    list-style-type: none;
  }
}

.tab-body {
  padding: var(--space-3) var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.is-busy {
  pointer-events: none;
  opacity: 0.6;
}

.tab-controls {
  padding: var(--space-3) var(--space-4) var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

div.record-panels {
  display: grid;
  gap: var(--space-3);
  @media only screen and (min-width: $fullhd) {
    gap: var(--space-4);
    grid-template-columns: 55% 1fr;
  }

  .details-box {
    position: relative;
    &:empty {
      display: none;
    }
  }
}

.sidebar-panels {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.modal-card {
  width: 80vw;
  max-height: 80vh;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
}

.flex-grow-1 {
  flex-grow: 1;
}
</style>
